// pages/dashboard/CompanyDetail.jsx
// Manage team members for a specific company
// Add members, send invitations, track completion

import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';

const copyToClipboard = (text, onSuccess, onFail) => {
  if (navigator.clipboard && window.isSecureContext) {
    navigator.clipboard.writeText(text).then(onSuccess).catch(onFail);
  } else {
    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.style.position = 'fixed';
    textarea.style.opacity = '0';
    document.body.appendChild(textarea);
    textarea.focus();
    textarea.select();
    try { document.execCommand('copy'); onSuccess(); } catch (err) { onFail(err); }
    document.body.removeChild(textarea);
  }
};

const CompanyDetail = () => {
  const { id } = useParams();
  const { admin, logout } = useAuth();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);

  const [company, setCompany] = useState(null);
  const [members, setMembers] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [showAddMember, setShowAddMember] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [sending, setSending] = useState(false);
  const [shareOpen, setShareOpen] = useState(null);
  const [newMember, setNewMember] = useState({ firstName: '', lastName: '', email: '', jobTitle: '' });

  useEffect(() => {
    fetchCompany();
  }, [id]);

  const fetchCompany = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`/enterprise/companies/${id}`);
      if (response.data.success) {
        setCompany(response.data.data.company);
        setMembers(response.data.data.members);
        setStats(response.data.data.stats);
      }
    } catch (err) {
      console.error('Fetch company error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleAddMember = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setMessage({ type: '', text: '' });
    try {
      const response = await axios.post(`/enterprise/companies/${id}/members`, newMember);
      if (response.data.success) {
        setMessage({ type: 'success', text: 'Member added successfully' });
        setNewMember({ firstName: '', lastName: '', email: '', jobTitle: '' });
        setShowAddMember(false);
        fetchCompany();
      }
    } catch (err) {
      setMessage({ type: 'error', text: err.response?.data?.error || 'Failed to add member' });
    } finally {
      setSubmitting(false);
    }
  };

  const handleCSVUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = async (event) => {
      const text = event.target.result;
      const lines = text.split('\n').filter(l => l.trim());
      const headers = lines[0].toLowerCase().split(',').map(h => h.trim());

      const members = lines.slice(1).map(line => {
        const values = line.split(',').map(v => v.trim().replace(/"/g, ''));
        const member = {};
        headers.forEach((h, i) => {
          if (h.includes('first')) member.firstName = values[i];
          else if (h.includes('last')) member.lastName = values[i];
          else if (h.includes('email')) member.email = values[i];
          else if (h.includes('title') || h.includes('role')) member.jobTitle = values[i];
        });
        return member;
      }).filter(m => m.email);

      if (members.length === 0) {
        setMessage({ type: 'error', text: 'No valid members found in CSV. Expected columns: First Name, Last Name, Email, Job Title' });
        return;
      }

      try {
        const response = await axios.post(`/enterprise/companies/${id}/members/bulk`, { members });
        if (response.data.success) {
          setMessage({ type: 'success', text: `CSV imported: ${response.data.data.added} added, ${response.data.data.skipped} skipped` });
          fetchCompany();
        }
      } catch (err) {
        setMessage({ type: 'error', text: 'CSV import failed' });
      }
    };
    reader.readAsText(file);
  };

  const handleSendInvitations = async (method) => {
    setSending(true);
    setMessage({ type: '', text: '' });
    try {
      const response = await axios.post(`/enterprise/companies/${id}/send-invitations`, { method });
      if (response.data.success) {
        setMessage({ type: 'success', text: `Sent ${response.data.data.sent} invitations` });
        fetchCompany();
      }
    } catch (err) {
      setMessage({ type: 'error', text: err.response?.data?.error || 'Failed to send invitations' });
    } finally {
      setSending(false);
    }
  };

  const handleSendReminders = async () => {
    setSending(true);
    try {
      const response = await axios.post(`/enterprise/companies/${id}/send-reminders`);
      if (response.data.success) {
        setMessage({ type: 'success', text: `Reminders sent to ${response.data.data.sent} members` });
      }
    } catch (err) {
      setMessage({ type: 'error', text: 'Failed to send reminders' });
    } finally {
      setSending(false);
    }
  };

  const handleRemoveMember = async (memberId) => {
    if (!window.confirm('Remove this member?')) return;
    try {
      await axios.delete(`/enterprise/members/${memberId}`);
      setMessage({ type: 'success', text: 'Member removed' });
      fetchCompany();
    } catch (err) {
      setMessage({ type: 'error', text: 'Failed to remove member' });
    }
  };

  const handleRegeneratePasscode = async () => {
    if (!window.confirm('Generate a new passcode? The old one will stop working.')) return;
    try {
      const response = await axios.post(`/enterprise/companies/${id}/regenerate-passcode`);
      if (response.data.success) {
        setMessage({ type: 'success', text: `New passcode: ${response.data.data.passcode}` });
        fetchCompany();
      }
    } catch (err) {
      setMessage({ type: 'error', text: 'Failed to regenerate passcode' });
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/admin/login');
  };

  const getStatusBadge = (status) => {
    const map = {
      not_invited: { bg: '#F4F6F9', color: '#7F8C8D', label: 'Not Invited' },
      invited: { bg: '#EBF5FB', color: '#1A5276', label: 'Invited' },
      started: { bg: '#FEF9E7', color: '#A04000', label: 'In Progress' },
      completed: { bg: '#E8F8EF', color: '#1E8449', label: 'Completed' },
    };
    const b = map[status] || { bg: '#F4F6F9', color: '#7F8C8D', label: status };
    return <span style={{ backgroundColor: b.bg, color: b.color, padding: '3px 10px', borderRadius: '100px', fontSize: '11px', fontWeight: '500' }}>{b.label}</span>;
  };

  const navItems = [
    { label: 'Dashboard', path: '/admin/dashboard', icon: '📊' },
    { label: 'Submissions', path: '/admin/submissions', icon: '📋' },
    { label: 'Transactions', path: '/admin/transactions', icon: '💳' },
    { label: 'Meetings', path: '/admin/meetings', icon: '📅' },
    { label: 'Enterprise', path: '/admin/enterprise', icon: '🏢', active: true },
    { label: 'Form Maker', path: '/admin/forms', icon: '📝' },
    { label: 'Analytics', path: '/admin/analytics', icon: '📈' },
    { label: 'Settings', path: '/admin/settings', icon: '⚙️' },
  ];

  if (loading) return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <p>Loading...</p>
    </div>
  );

  return (
    <div style={s.page}>
      <div style={s.sidebar}>
        <div style={s.sidebarLogo}>
          <span style={s.logoExec}>EXECUTIVE</span>
          <span style={s.logoSpring}>Springboard</span>
        </div>
        <nav style={s.nav}>
          {navItems.map(item => (
            <button key={item.path} style={{ ...s.navItem, ...(item.active ? s.navActive : {}) }} onClick={() => navigate(item.path)}>
              <span>{item.icon}</span><span>{item.label}</span>
            </button>
          ))}
        </nav>
        <div style={s.sidebarFooter}>
          <div style={s.adminName}>{admin?.name}</div>
          <button style={s.logoutBtn} onClick={handleLogout}>Sign Out</button>
        </div>
      </div>

      <div style={s.main}>
        <button style={s.backBtn} onClick={() => navigate('/admin/enterprise')}>← Back to Enterprise</button>

        {/* Company Header */}
        <div style={s.companyHeader}>
          <div>
            <h1 style={s.title}>{company?.companyName}</h1>
            <p style={s.subtitle}>{company?.industry || 'No industry'} · {company?.contactEmail}</p>
          </div>
          <div style={s.headerActions}>
            <div style={s.passcodeDisplay}>
              <span style={s.passcodeLabel}>Company Code:</span>
              <span style={s.passcodeValue}>{company?.passcode}</span>
              <button style={s.btnRegenerate} onClick={handleRegeneratePasscode}>Regenerate</button>
            </div>
          </div>
        </div>

        {/* Stats */}
        {stats && (
          <div style={s.statsGrid}>
            {[
              { label: 'Total Members', value: stats.total, color: '#0A2342' },
              { label: 'Not Invited', value: stats.notInvited, color: '#7F8C8D' },
              { label: 'Invited', value: stats.invited, color: '#1A5276' },
              { label: 'In Progress', value: stats.started, color: '#A04000' },
              { label: 'Completed', value: stats.completed, color: '#27AE60' },
            ].map((stat, i) => (
              <div key={i} style={s.statCard}>
                <div style={{ ...s.statValue, color: stat.color }}>{stat.value}</div>
                <div style={s.statLabel}>{stat.label}</div>
              </div>
            ))}
          </div>
        )}

        {message.text && (
          <div style={{ ...s.messageBox, ...(message.type === 'success' ? s.messageSuccess : s.messageError) }}>
            {message.text}
          </div>
        )}

        {/* Action Buttons */}
        <div style={s.actionsRow}>
          <button style={s.btnAction} onClick={() => setShowAddMember(!showAddMember)}>
            + Add Member
          </button>
          <button style={s.btnAction} onClick={() => fileInputRef.current?.click()}>
            📤 Import CSV
          </button>
          <input ref={fileInputRef} type="file" accept=".csv" style={{ display: 'none' }} onChange={handleCSVUpload} />
          <button style={s.btnActionTeal} onClick={() => handleSendInvitations('passcode')} disabled={sending}>
            {sending ? 'Sending...' : '📧 Send Passcode Invites'}
          </button>
          <button style={s.btnActionTeal} onClick={() => handleSendInvitations('link')} disabled={sending}>
            {sending ? 'Sending...' : '🔗 Send Direct Links'}
          </button>
          <button style={s.btnActionOutline} onClick={handleSendReminders} disabled={sending}>
            🔔 Send Reminders
          </button>
        </div>

        {/* CSV Template note */}
        <p style={s.csvNote}>
          CSV format: First Name, Last Name, Email, Job Title (header row required)
        </p>

        {/* Add Member Form */}
        {showAddMember && (
          <div style={s.addMemberForm}>
            <h3 style={s.addMemberTitle}>Add New Member</h3>
            <form onSubmit={handleAddMember}>
              <div style={s.formRow}>
                <div style={s.formGroup}>
                  <label style={s.label}>First Name *</label>
                  <input style={s.input} type="text" value={newMember.firstName} onChange={e => setNewMember(p => ({ ...p, firstName: e.target.value }))} required />
                </div>
                <div style={s.formGroup}>
                  <label style={s.label}>Last Name *</label>
                  <input style={s.input} type="text" value={newMember.lastName} onChange={e => setNewMember(p => ({ ...p, lastName: e.target.value }))} required />
                </div>
                <div style={s.formGroup}>
                  <label style={s.label}>Email *</label>
                  <input style={s.input} type="email" value={newMember.email} onChange={e => setNewMember(p => ({ ...p, email: e.target.value }))} required />
                </div>
                <div style={s.formGroup}>
                  <label style={s.label}>Job Title</label>
                  <input style={s.input} type="text" value={newMember.jobTitle} onChange={e => setNewMember(p => ({ ...p, jobTitle: e.target.value }))} />
                </div>
              </div>
              <button type="submit" style={s.btnSubmit} disabled={submitting}>
                {submitting ? 'Adding...' : 'Add Member'}
              </button>
            </form>
          </div>
        )}

        {/* Members Table */}
        <div style={s.card}>
          <h2 style={s.cardTitle}>Team Members ({members.length})</h2>
          {members.length === 0 ? (
            <p style={s.emptyText}>No members added yet. Add members manually or import a CSV file.</p>
          ) : (
            <div>
              <div style={s.tableHead}>
                <span>Name</span>
                <span>Email</span>
                <span>Job Title</span>
                <span>Status</span>
                <span>Invited</span>
                <span>Action</span>
              </div>
              {members.map(member => (
                <div key={member._id} style={s.tableRow}>
                  <span style={s.memberName}>{member.firstName} {member.lastName}</span>
                  <span style={s.cell}>{member.email}</span>
                  <span style={s.cell}>{member.jobTitle || 'N/A'}</span>
                  <span style={s.cell}>{getStatusBadge(member.status)}</span>
                  <span style={s.cell}>
                    {member.invitationSentAt
                      ? new Date(member.invitationSentAt).toLocaleDateString()
                      : 'Not yet'
                    }
                  </span>
                  <span style={s.cell}>
                    <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                      {/* Share dropdown */}
                      <div style={{ position: 'relative' }}>
                        <button
                          style={s.btnShare}
                          onClick={() => setShareOpen(shareOpen === member._id ? null : member._id)}
                        >
                          Share ▾
                        </button>
                        {shareOpen === member._id && (
                          <div style={s.shareDropdown}>
                            <button
                              style={s.shareOption}
                              onClick={() => {
                                const link = `${window.location.origin}/start?code=${company?.passcode}`;
                                copyToClipboard(
                                  link,
                                  () => setMessage({ type: 'success', text: 'Assessment link copied!' }),
                                  () => setMessage({ type: 'error', text: 'Could not copy. Please copy manually.' })
                                );
                                setShareOpen(null);
                              }}
                            >
                              📋 Copy Assessment Link
                            </button>
                            <button
                              style={s.shareOption}
                              onClick={async () => {
                                try {
                                  await axios.post(`/enterprise/companies/${id}/send-invitations`, {
                                    method: 'passcode',
                                    memberIds: [member._id],
                                  });
                                  setMessage({ type: 'success', text: `Invitation sent to ${member.email}` });
                                } catch (err) {
                                  setMessage({ type: 'error', text: 'Failed to send email' });
                                }
                                setShareOpen(null);
                              }}
                            >
                              📧 Send via Email
                            </button>
                          </div>
                        )}
                      </div>
                      {member.submissionId && (
                        <button
                          style={s.btnView}
                          onClick={() => navigate(`/admin/submissions/${member.submissionId}`)}
                        >
                          View Report
                        </button>
                      )}
                      <button style={s.btnRemove} onClick={() => handleRemoveMember(member._id)}>
                        Remove
                      </button>
                    </div>
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const s = {
  page: { display: 'flex', minHeight: '100vh', fontFamily: 'var(--font-primary)' },
  sidebar: { width: '240px', backgroundColor: 'var(--color-primary)', display: 'flex', flexDirection: 'column', flexShrink: 0 },
  sidebarLogo: { display: 'flex', flexDirection: 'column', gap: '2px', padding: '24px 20px', borderBottom: '1px solid rgba(255,255,255,0.1)' },
  logoExec: { fontSize: '9px', fontWeight: '700', letterSpacing: '3px', color: 'var(--color-teal)', lineHeight: 1 },
  logoSpring: { fontSize: '18px', fontWeight: '700', color: '#fff', lineHeight: 1 },
  nav: { flex: 1, padding: '16px 0' },
  navItem: { width: '100%', display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 20px', background: 'none', border: 'none', color: 'rgba(255,255,255,0.6)', fontSize: '14px', cursor: 'pointer', textAlign: 'left' },
  navActive: { backgroundColor: 'rgba(255,255,255,0.1)', color: '#fff' },
  sidebarFooter: { padding: '16px 20px', borderTop: '1px solid rgba(255,255,255,0.1)' },
  adminName: { fontSize: '13px', fontWeight: '600', color: '#fff', marginBottom: '10px' },
  logoutBtn: { width: '100%', padding: '8px', backgroundColor: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '6px', color: 'rgba(255,255,255,0.5)', fontSize: '12px', cursor: 'pointer' },
  main: { flex: 1, backgroundColor: 'var(--color-dashboard-bg)', padding: '24px 32px', overflowY: 'auto' },
  backBtn: { background: 'none', border: 'none', color: 'var(--color-teal)', fontSize: '14px', cursor: 'pointer', marginBottom: '20px', fontWeight: '500', padding: 0 },
  companyHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', backgroundColor: '#fff', borderRadius: '12px', border: '1px solid var(--color-border)', padding: '20px 24px', marginBottom: '20px', flexWrap: 'wrap', gap: '16px' },
  title: { fontSize: '24px', fontWeight: '700', color: 'var(--color-primary)', marginBottom: '4px' },
  subtitle: { fontSize: '14px', color: 'var(--color-text-light)' },
  headerActions: {},
  passcodeDisplay: { display: 'flex', alignItems: 'center', gap: '10px', backgroundColor: 'var(--color-off-white)', borderRadius: '8px', padding: '10px 14px' },
  passcodeLabel: { fontSize: '12px', color: 'var(--color-text-light)' },
  passcodeValue: { fontSize: '18px', fontWeight: '800', color: 'var(--color-teal)', letterSpacing: '2px', fontFamily: 'monospace' },
  btnRegenerate: { background: 'none', border: '1px solid var(--color-border)', color: 'var(--color-text-light)', padding: '4px 10px', borderRadius: '6px', fontSize: '11px', cursor: 'pointer' },
  statsGrid: { display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '12px', marginBottom: '20px' },
  statCard: { backgroundColor: '#fff', borderRadius: '10px', border: '1px solid var(--color-border)', padding: '16px', textAlign: 'center' },
  statValue: { fontSize: '28px', fontWeight: '700', marginBottom: '4px' },
  statLabel: { fontSize: '11px', color: 'var(--color-text-light)', textTransform: 'uppercase', letterSpacing: '0.5px' },
  messageBox: { padding: '12px 16px', borderRadius: '8px', fontSize: '14px', marginBottom: '16px' },
  messageSuccess: { backgroundColor: 'var(--color-success-bg)', color: 'var(--color-success)', border: '1px solid #A9DFBF' },
  messageError: { backgroundColor: 'var(--color-error-bg)', color: 'var(--color-error)', border: '1px solid #F5C6CB' },
  actionsRow: { display: 'flex', gap: '10px', marginBottom: '8px', flexWrap: 'wrap' },
  btnAction: { backgroundColor: '#fff', color: 'var(--color-text)', padding: '10px 16px', borderRadius: '8px', border: '1px solid var(--color-border)', fontSize: '13px', fontWeight: '500', cursor: 'pointer' },
  btnActionTeal: { backgroundColor: 'var(--color-teal)', color: '#fff', padding: '10px 16px', borderRadius: '8px', border: 'none', fontSize: '13px', fontWeight: '500', cursor: 'pointer' },
  btnActionOutline: { backgroundColor: 'transparent', color: 'var(--color-primary)', padding: '10px 16px', borderRadius: '8px', border: '1px solid var(--color-primary)', fontSize: '13px', fontWeight: '500', cursor: 'pointer' },
  csvNote: { fontSize: '12px', color: 'var(--color-text-light)', marginBottom: '16px', fontStyle: 'italic' },
  addMemberForm: { backgroundColor: '#fff', borderRadius: '10px', border: '1px solid var(--color-border)', padding: '20px', marginBottom: '16px' },
  addMemberTitle: { fontSize: '15px', fontWeight: '600', color: 'var(--color-primary)', marginBottom: '16px' },
  formRow: { display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '12px' },
  formGroup: { marginBottom: '12px' },
  label: { display: 'block', fontSize: '12px', fontWeight: '600', color: 'var(--color-text)', marginBottom: '4px' },
  input: { width: '100%', padding: '9px 12px', border: '1.5px solid var(--color-border)', borderRadius: '7px', fontSize: '13px', color: 'var(--color-text)', outline: 'none', boxSizing: 'border-box' },
  btnSubmit: { backgroundColor: 'var(--color-teal)', color: '#fff', padding: '10px 24px', borderRadius: '8px', border: 'none', fontSize: '13px', fontWeight: '600', cursor: 'pointer' },
  card: { backgroundColor: '#fff', borderRadius: '12px', border: '1px solid var(--color-border)', padding: '24px' },
  cardTitle: { fontSize: '16px', fontWeight: '600', color: 'var(--color-primary)', marginBottom: '16px' },
  emptyText: { fontSize: '14px', color: 'var(--color-text-light)', textAlign: 'center', padding: '20px' },
  tableHead: { display: 'grid', gridTemplateColumns: '1.2fr 1.5fr 1fr 0.8fr 0.7fr 1fr', gap: '12px', padding: '8px 12px', fontSize: '11px', fontWeight: '600', color: 'var(--color-text-light)', textTransform: 'uppercase', letterSpacing: '1px', borderBottom: '1px solid var(--color-border)', marginBottom: '4px' },
  tableRow: { display: 'grid', gridTemplateColumns: '1.2fr 1.5fr 1fr 0.8fr 0.7fr 1fr', gap: '12px', padding: '12px', borderRadius: '8px', backgroundColor: 'var(--color-off-white)', alignItems: 'center', marginBottom: '4px' },
  memberName: { fontSize: '13px', fontWeight: '600', color: 'var(--color-primary)' },
  cell: { fontSize: '12px', color: 'var(--color-text-light)' },
  btnView: { backgroundColor: 'var(--color-teal)', color: '#fff', padding: '4px 10px', borderRadius: '5px', border: 'none', fontSize: '11px', cursor: 'pointer' },
  btnRemove: { backgroundColor: 'var(--color-error-bg)', color: 'var(--color-error)', padding: '4px 10px', borderRadius: '5px', border: '1px solid var(--color-error)', fontSize: '11px', cursor: 'pointer' },
  btnShare: { backgroundColor: 'var(--color-primary)', color: '#fff', padding: '4px 10px', borderRadius: '5px', border: 'none', fontSize: '11px', cursor: 'pointer' },
  shareDropdown: { position: 'absolute', top: '100%', left: 0, backgroundColor: '#fff', border: '1px solid var(--color-border)', borderRadius: '8px', boxShadow: '0 4px 12px rgba(0,0,0,0.15)', zIndex: 100, minWidth: '180px', overflow: 'hidden' },
  shareOption: { width: '100%', padding: '10px 14px', border: 'none', backgroundColor: 'transparent', textAlign: 'left', fontSize: '13px', cursor: 'pointer', color: 'var(--color-text)', display: 'block' },
};

export default CompanyDetail;
